import React, { useState, useEffect } from 'react';
import { ExternalLink, AlertTriangle, X } from 'lucide-react';
import { getJobberAuthUrl } from '../services/jobberConfig';
import { JobberService } from '../services/jobberService';
import syncService from '../services/syncService';

const DisconnectionBubble = ({ show, onClose, onReconnectSuccess, userId }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [isReconnecting, setIsReconnecting] = useState(false);
  const [queueLength, setQueueLength] = useState(0);

  useEffect(() => {
    if (show) {
      setIsVisible(true);
      // Update queue length when bubble is shown
      if (userId) {
        const status = syncService.getJobberSyncQueueStatus(userId);
        setQueueLength(status.queueLength);
      }
    }
  }, [show, userId]);

  const handleReconnect = async () => {
    setIsReconnecting(true);
    
    try {
      // Generate OAuth URL and open popup (same as settings page)
      const authUrl = getJobberAuthUrl(userId);
      console.log('Generated Jobber Auth URL for reconnection:', authUrl);
      
      const popup = window.open(authUrl, 'jobber-oauth-reconnect', 'width=600,height=700,scrollbars=yes,resizable=yes');
      
      if (!popup) {
        throw new Error('Popup blocked. Please allow popups for this site.');
      }

      // Listen for popup close
      const checkClosed = setInterval(() => {
        if (popup.closed) {
          clearInterval(checkClosed);
          setIsReconnecting(false);
          if (userId) {
            JobberService.loadTokensForUser(userId).then(() => {
              if (JobberService.isAuthenticated()) {
                console.log('✅ Reconnection completed successfully');
                syncService.processJobberSyncQueue();
                onReconnectSuccess();
                handleClose();
              } else {
                console.log('❌ Reconnection not completed - popup closed without success');
              }
            });
          } else {
            if (JobberService.isAuthenticated()) {
              syncService.processJobberSyncQueue();
              onReconnectSuccess();
              handleClose();
            }
          }
        }
      }, 1000);

      const handleMessage = (event) => {
        if (event.origin !== window.location.origin) return;

        if (event.data.type === 'JOBBER_OAUTH_SUCCESS') {
          console.log('✅ Received OAuth success message during reconnection');
          clearInterval(checkClosed);
          setIsReconnecting(false);
          if (userId) {
            JobberService.loadTokensForUser(userId).then(() => {
              syncService.processJobberSyncQueue();
              onReconnectSuccess();
              handleClose();
            });
          } else {
            syncService.processJobberSyncQueue();
            onReconnectSuccess();
            handleClose();
          }
        } else if (event.data.type === 'JOBBER_OAUTH_ERROR') {
          console.log('❌ Received OAuth error message during reconnection:', event.data.error);
          clearInterval(checkClosed);
          setIsReconnecting(false);
        }
      };

      window.addEventListener('message', handleMessage);
      
      // Cleanup
      setTimeout(() => {
        clearInterval(checkClosed);
        window.removeEventListener('message', handleMessage);
      }, 300000); // 5 minutes timeout
      
    } catch (error) {
      console.error('❌ Reconnection error:', error);
      setIsReconnecting(false);
    }
  };

  const handleClose = () => {
    setIsVisible(false);
    setTimeout(onClose, 300); // Wait for fade out animation
  };

  const handleIgnore = () => {
    handleClose();
  };

  if (!show && !isVisible) return null;

  return (
    <div
      className={`fixed inset-0 z-[9999] flex items-center justify-center transition-all duration-300 ${
        isVisible ? 'opacity-100' : 'opacity-0 pointer-events-none'
      }`}
      style={{ zIndex: 9999 }}
    >
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black bg-opacity-50"
        onClick={handleIgnore}
      />
      
      {/* Bubble */}
      <div
        className={`relative bg-white rounded-lg shadow-xl p-6 max-w-md mx-4 transform transition-all duration-300 ${
          isVisible ? 'scale-100' : 'scale-95'
        }`}
      >
        {/* Close button */}
        <button
          onClick={handleClose}
          className="absolute top-2 right-2 text-gray-400 hover:text-gray-600 transition-colors"
        >
          <X className="w-5 h-5" />
        </button>

        {/* Content */}
        <div className="text-center">
          <div className="flex justify-center mb-4">
            <AlertTriangle className="w-12 h-12 text-orange-500" />
          </div>
          
          <h3 className="text-xl font-semibold text-gray-900 mb-3">
            Not Connected to Jobber
          </h3>
          
          <p className="text-gray-600 mb-6 text-lg">
            {queueLength > 0 
              ? `You have ${queueLength} quote${queueLength > 1 ? 's' : ''} saved locally but not synced to Jobber. Would you like to reconnect and sync ${queueLength > 1 ? 'them' : 'it'}?`
              : 'This quote was saved locally but not synced to Jobber. Would you like to reconnect and sync this quote?'
            }
          </p>

          {/* Buttons */}
          <div className="flex space-x-4 justify-center">
            <button
              onClick={handleReconnect}
              disabled={isReconnecting}
              className="flex items-center space-x-2 bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-semibold"
            >
              <ExternalLink className="w-5 h-5" />
              <span>{isReconnecting ? 'Connecting...' : 'Re-connect to Jobber'}</span>
            </button>
            
            <button
              onClick={handleIgnore}
              className="px-6 py-3 text-gray-600 hover:text-gray-800 transition-colors font-semibold border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              Ignore
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DisconnectionBubble;